import React, { useState, useEffect } from "react";
import { Upload, message, Modal, Image } from "antd";
import type { UploadFile, UploadProps } from "antd/es/upload";
import { PlusOutlined, EyeOutlined, DeleteOutlined } from "@ant-design/icons";
import { adminFileUploadAPI } from "../utils/fileUploadUtils";

interface ImageUploadProps {
  value?: string; // 当前图片URL
  onChange?: (url: string | undefined) => void; // 图片变化回调
  maxCount?: number; // 最大上传数量，默认1
  accept?: string; // 接受的文件类型
  listType?: "text" | "picture" | "picture-card"; // 上传列表的内建样式
  disabled?: boolean; // 是否禁用
  uploadPath?: string; // 上传路径前缀，对应UniApp中的filePath参数
}

/**
 * 图片上传组件
 * 使用新的文件上传工具类 adminFileUploadAPI，
 * 模仿UniApp中的AvatarUpload.vue和wechatCommonServices.file.uploadImage实现
 * 支持预签名URL上传到腾讯云COS，与UniApp版本功能一致
 *
 * @example
 * <ImageUpload
 *   value={avatarUrl}
 *   onChange={setAvatarUrl}
 *   uploadPath="avatars/"
 *   accept="image/*"
 * />
 */
const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  maxCount = 1,
  accept = "image/*",
  listType = "picture-card",
  disabled = false,
  uploadPath = "uploads/",
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>(() => {
    if (value) {
      return [
        {
          uid: "-1",
          name: "image",
          status: "done",
          url: value,
        },
      ];
    }
    return [];
  });

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  // 监听value变化，同步更新fileList
  useEffect(() => {
    if (value) {
      setFileList([
        {
          uid: "-1",
          name: "image",
          status: "done",
          url: value,
        },
      ]);
    } else {
      setFileList([]);
    }
  }, [value]);

  // 文件上传前的检查，使用新的文件验证工具，参考UniApp中的beforeUpload逻辑
  const beforeUpload = (file: File): boolean => {
    const validation = adminFileUploadAPI.validateFile(file, {
      allowedTypes: [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ],
      maxSizeInMB: 5,
    });

    if (!validation.valid) {
      message.error(validation.error || "文件验证失败");
      return false;
    }

    return true;
  };

  // 自定义上传方法，使用新的文件上传工具类，模仿UniApp中的wechatCommonServices.file.uploadImage实现
  const customUpload = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;

    try {
      // 先验证文件
      const validation = adminFileUploadAPI.validateFile(file, {
        allowedTypes: [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/gif",
          "image/webp",
        ],
        maxSizeInMB: 5,
      });

      if (!validation.valid) {
        message.error(validation.error || "文件验证失败");
        onError(new Error(validation.error || "文件验证失败"));
        return;
      }

      // 生成唯一文件名，参考UniApp中的命名策略
      const timestamp = Date.now();
      const randomSuffix = Math.random().toString(36).substring(2, 8);
      const fileExtension = file.name.split(".").pop() || "jpg";
      const filename = `upload_${timestamp}_${randomSuffix}.${fileExtension}`;

      // 使用新的文件上传工具类
      const fileUrl = await adminFileUploadAPI.uploadImage(
        file,
        {
          filename,
          filePath: uploadPath, // 对应UniApp中的filePath参数
        },
        (progress) => {
          // 上传进度回调，类似UniApp中的进度处理
          onProgress({ percent: progress.percent });
        }
      );

      // 上传成功处理
      if (fileUrl) {
        onSuccess({ url: fileUrl }, file);
        message.success("图片上传成功");

        // 更新父组件的值，类似UniApp中的emit("update:avatar", uploadedUrl)
        if (onChange) {
          onChange(fileUrl);
        }
      } else {
        throw new Error("上传返回的URL为空");
      }
    } catch (error) {
      console.error("图片上传失败:", error);
      onError(error);
      message.error("图片上传失败，请重试");

      // 失败时恢复原值，参考UniApp中的错误处理
      if (onChange && value) {
        onChange(value);
      }
    }
  };

  const handleChange: UploadProps["onChange"] = ({ fileList: newFileList }) => {
    setFileList(newFileList);

    // 如果列表为空，清空值
    if (newFileList.length === 0 && onChange) {
      onChange(undefined);
    }
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as File);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewVisible(true);
    setPreviewTitle(
      file.name || file.url!.substring(file.url!.lastIndexOf("/") + 1)
    );
  };

  const handleRemove = (file: UploadFile) => {
    if (onChange) {
      onChange(undefined);
    }
    return true;
  };

  // 获取文件Base64预览
  const getBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>上传</div>
    </div>
  );

  // 自定义图片预览和删除按钮
  const itemRender = (originNode: React.ReactElement, file: UploadFile) => {
    return (
      <div className="upload-item-container">
        {file.status === "done" && file.url ? (
          <div className="upload-item-preview">
            <Image
              width={80}
              height={80}
              src={file.url}
              style={{ objectFit: "cover", borderRadius: 8 }}
              preview={false}
            />
            <div className="upload-item-mask">
              <EyeOutlined
                onClick={() => handlePreview(file)}
                style={{ color: "white", marginRight: 8, cursor: "pointer" }}
              />
              {!disabled && (
                <DeleteOutlined
                  onClick={() => handleRemove(file)}
                  style={{ color: "white", cursor: "pointer" }}
                />
              )}
            </div>
          </div>
        ) : (
          originNode
        )}
      </div>
    );
  };

  return (
    <>
      <Upload
        customRequest={customUpload}
        listType={listType}
        fileList={fileList}
        onPreview={handlePreview}
        onChange={handleChange}
        onRemove={handleRemove}
        beforeUpload={beforeUpload}
        maxCount={maxCount}
        accept={accept}
        disabled={disabled}
        itemRender={listType === "picture-card" ? itemRender : undefined}
      >
        {fileList.length >= maxCount ? null : uploadButton}
      </Upload>

      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
      >
        <img alt="preview" style={{ width: "100%" }} src={previewImage} />
      </Modal>

      <style>{`
        .upload-item-container {
          position: relative;
          display: inline-block;
        }

        .upload-item-preview {
          position: relative;
          display: inline-block;
        }

        .upload-item-mask {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s;
          border-radius: 8px;
        }

        .upload-item-preview:hover .upload-item-mask {
          opacity: 1;
        }
      `}</style>
    </>
  );
};

export default ImageUpload;
